/**
 * PR-35B — Bridge between the PR-35A poll lifecycle engine and the live
 * widgets (LivePollWidget, PredictionCard, community polls, CTAs).
 *
 * Deterministic, no generative AI. All persistence is delegated to the
 * PR-35A engine. MODO DEMO is not affected.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { trackEvent } from "@/lib/audit";
import {
  getLifecycleConfig,
  hasAnswered,
  markAnswered,
  recordPredictionResult,
  shouldShowPoll,
} from "@/lib/pr35a/pollLifecycle";
import { celebrate } from "@/lib/pr32/celebrationEngine";
import { awardPoints } from "@/lib/gamification";

const ACTIVE_MATCH_KEY = "vs:active_match_id";
const FALLBACK_MATCH_ID = "live";

/** Best-effort active matchId for dedup scoping. */
export function getActiveMatchIdForPolls(): string {
  if (typeof window === "undefined") return FALLBACK_MATCH_ID;
  try {
    const v = window.localStorage.getItem(ACTIVE_MATCH_KEY);
    return v && v.length > 0 ? v : FALLBACK_MATCH_ID;
  } catch {
    return FALLBACK_MATCH_ID;
  }
}

/** Set by upstream surfaces when the active match changes. */
export function setActiveMatchIdForPolls(id: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (id) window.localStorage.setItem(ACTIVE_MATCH_KEY, id);
    else window.localStorage.removeItem(ACTIVE_MATCH_KEY);
  } catch { /* noop */ }
}

export interface DisplayCheck {
  matchId?: string;
  pollType: string;
  question: string;
}

/** Returns true when the widget is allowed to render this poll/prediction. */
export function canDisplay(input: DisplayCheck): boolean {
  const matchId = input.matchId ?? getActiveMatchIdForPolls();
  return shouldShowPoll(matchId, input.pollType, input.question);
}

/** Returns true when already answered for this match. */
export function isAlreadyAnswered(input: DisplayCheck): boolean {
  const matchId = input.matchId ?? getActiveMatchIdForPolls();
  return hasAnswered(matchId, input.pollType, input.question);
}

/** Register the vote so the same question is not shown again this match. */
export function registerAnswer(input: DisplayCheck): void {
  const matchId = input.matchId ?? getActiveMatchIdForPolls();
  markAnswered(matchId, input.pollType, input.question);
}

/** Post-vote auto-dismiss delay in ms, derived from the lifecycle config. */
export function postVoteDismissMs(): number {
  return Math.max(1_500, getLifecycleConfig().postVoteSec * 1000);
}

/**
 * Resolve outcome of a poll/prediction. Celebrates on hit, otherwise
 * registers the result silently. Returns the awarded points (0 on miss).
 */
export function resolveOutcome(opts: {
  correct: boolean;
  surface: "live_poll" | "prediction_card" | "community_poll";
  questionId?: string;
  pollType?: string;
}): number {
  const cfg = getLifecycleConfig();
  const stats = recordPredictionResult({ correct: opts.correct });
  if (!opts.correct) return 0;
  void awardPoints("prediction_correct", {
    surface: opts.surface,
    question_id: opts.questionId,
    poll_type: opts.pollType,
    points: cfg.pointsOnCorrect,
  });
  void trackEvent("celebration_shown", {
    surface: opts.surface,
    points: cfg.pointsOnCorrect,
  });
  celebrate("points_won", { vibrate: true, sound: cfg.confettiEnabled });
  toast.success("🎉 ¡Acertaste!", {
    description: `+${cfg.pointsOnCorrect} pts · Precisión ${stats.accuracyPct}%`,
    duration: cfg.celebrationSec * 1000,
  });
  return cfg.pointsOnCorrect;
}

/** React hook returning a stable answered/can-display tuple for a question. */
export function usePollGate(input: DisplayCheck): {
  answered: boolean;
  canShow: boolean;
  matchId: string;
} {
  const matchId = input.matchId ?? getActiveMatchIdForPolls();
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick((t) => t + 1);
    if (typeof window === "undefined") return;
    window.addEventListener("storage", fn);
    return () => window.removeEventListener("storage", fn);
  }, []);
  return {
    matchId,
    answered: hasAnswered(matchId, input.pollType, input.question),
    canShow: shouldShowPoll(matchId, input.pollType, input.question),
  };
}

export const PR35B_QA_ITEMS: ReadonlyArray<{ id: string; label: string }> = [
  { id: "gate", label: "Widgets consultan canDisplay() antes de mostrar." },
  { id: "dedup", label: "Misma pregunta no reaparece tras responder." },
  { id: "register", label: "Voto/respuesta queda registrada por partido." },
  { id: "auto", label: "Encuesta/predicción se cierra entre 2–4 s tras votar." },
  { id: "celebrate", label: "Celebración + puntos al acertar la resolución." },
  { id: "zero-overlap", label: "Posición respeta Zero Overlap (PR-30)." },
  { id: "demo", label: "MODO DEMO intacto." },
  { id: "tsc", label: "tsc --noEmit = 0 errores." },
];